/**
 * The unread-messages badge (Round 120): one module-level number, refreshed from the server when the app mounts the navigation,
 * when the window regains focus, and once a minute while the tab is visible. A failed refresh keeps the last known number (a badge
 * that flickers to 0 on a network blip would hide real messages). Sign-out clears it.
 */
import { useSyncExternalStore } from 'react';

let unread = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function setUnread(n: number): void { if (n !== unread) { unread = Math.max(0, n); emit(); } }
export function clearUnread(): void { setUnread(0); }

export async function refreshUnread(): Promise<void> {
  // apiProxy is imported on use: the navigation (rendered on every learner page, and in many tests) must not pull the API client in at load
  try { const api = await import('@/core/apiProxy'); setUnread((await api.getInboxUnread()).unread); } catch { /* keep the last number */ }
}

export function useUnreadMessages(): number {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, () => unread, () => 0);
}

/** Starts the refresh triggers; returns the stop function. Called by Navigation while it is mounted. */
export function startUnreadPolling(intervalMs = 60_000): () => void {
  void refreshUnread();
  const onFocus = () => { void refreshUnread(); };
  const onVisible = () => { if (document.visibilityState === 'visible') void refreshUnread(); };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => { if (document.visibilityState === 'visible') void refreshUnread(); }, intervalMs);
  return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible); clearInterval(timer); };
}
